import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiSave, FiImage, FiType, FiAlignLeft, FiCheckCircle, FiGlobe, FiX, FiBriefcase } from 'react-icons/fi';

const InputCard = ({ icon, title, description, children }) => (
  <div className="p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300">
    <div className="flex items-start gap-4 mb-6">
      <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0 border border-orange-500/20">
        <SafeIcon icon={icon} className="text-orange-400 text-lg" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-amber-50 mb-1">{title}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed">{description}</p>
      </div>
    </div>
    <div className="pl-14">
      {children}
    </div>
  </div>
);

const SEO = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [ogImageFile, setOgImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const [isDomainVerified, setIsDomainVerified] = useState(false);

  const [seoData, setSeoData] = useState({
    title: '',
    description: '',
    ogImage: '',
    companyName: '',
    companyUrl: '',
    companyLogoUrl: ''
  });

  useEffect(() => {
    if (!user?.id) return;

    const fetchSeoData = async () => {
      try {
        const [seoRes, integRes] = await Promise.all([
          supabase.from('seo_settings').select('*').eq('user_id', user.id).maybeSingle(),
          supabase.from('user_integrations').select('is_domain_verified, verified_domain').eq('user_id', user.id).maybeSingle()
        ]);

        const verified = integRes.data?.is_domain_verified || false;
        const vDomain = integRes.data?.verified_domain || '';
        setIsDomainVerified(verified);

        let initialCompanyUrl = '';
        if (verified && vDomain) {
          initialCompanyUrl = vDomain;
        } else if (seoRes.data?.company_url) {
          initialCompanyUrl = seoRes.data.company_url;
        }

        const fetchedCompany = seoRes.data?.company_name || '';
        const fallbackCompanyName = fetchedCompany || 'Your Company';
        const fallbackTitle = `${fallbackCompanyName} Reviews & Testimonials | Moment of Truth`;
        const fallbackDesc = `Explore real reviews and testimonials from ${fallbackCompanyName} customers. A simple, transparent Wall of Love built from genuine client feedback.`;

        setSeoData({
          title: seoRes.data?.meta_title || fallbackTitle,
          description: seoRes.data?.meta_description || fallbackDesc,
          ogImage: seoRes.data?.og_image_url || '',
          companyName: fetchedCompany,
          companyUrl: initialCompanyUrl,
          companyLogoUrl: seoRes.data?.company_logo_url || ''
        });
      } catch (err) {
        console.error('Error fetching SEO data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSeoData();
  }, [user?.id]);

  const handleSave = async () => {
    if (!user?.id) return;

    let domainToSave = seoData.companyUrl.trim();
    if (domainToSave && !isDomainVerified) {
      // Clean up the domain (remove http/https and paths)
      domainToSave = domainToSave.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
      const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      if (!domainRegex.test(domainToSave)) {
        alert('Please enter a valid domain format (e.g., acme.com)');
        return;
      }
    }

    setIsSaving(true);

    try {
      // Handle Company Logo Upload
      let finalLogoUrl = seoData.companyLogoUrl;
      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const filePath = `${user.id}/logo-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('user-companies')
          .upload(filePath, logoFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('user-companies')
          .getPublicUrl(filePath);

        finalLogoUrl = `${publicUrl}?t=${Date.now()}`;
      }

      // Handle Open Graph Image Upload
      let finalOgImageUrl = seoData.ogImage;
      if (ogImageFile) {
        const fileExt = ogImageFile.name.split('.').pop();
        const filePath = `${user.id}/og-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('og-images')
          .upload(filePath, ogImageFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('og-images')
          .getPublicUrl(filePath);

        finalOgImageUrl = `${publicUrl}?t=${Date.now()}`;
      }

      const { error } = await supabase.from('seo_settings').upsert({
        user_id: user.id,
        meta_title: seoData.title,
        meta_description: seoData.description,
        og_image_url: finalOgImageUrl,
        company_name: seoData.companyName,
        company_url: domainToSave,
        company_logo_url: finalLogoUrl,
        created_at: new Date().toISOString()
      }, { onConflict: 'user_id' });

      if (error) throw error;

      setSeoData(prev => ({
        ...prev,
        companyUrl: domainToSave,
        ogImage: finalOgImageUrl,
        companyLogoUrl: finalLogoUrl
      }));
      setOgImageFile(null);
      setLogoFile(null);

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageSelect = (e, field) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    if (field === 'companyLogoUrl') {
      if (file.size > 150 * 1024) {
        alert('Company logo must be under 150KB.');
        // Reset input value
        if (logoInputRef.current) logoInputRef.current.value = '';
        return;
      }
      setLogoFile(file);
      setSeoData({ ...seoData, companyLogoUrl: URL.createObjectURL(file) });
    } else if (field === 'ogImage') {
      if (file.size > 400 * 1024) {
        alert('Open Graph image must be under 400KB.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setOgImageFile(file);
      setSeoData({ ...seoData, ogImage: URL.createObjectURL(file) });
    }
  };

  const defaultCompanyName = seoData.companyName || 'Your Company';
  const defaultTitle = `${defaultCompanyName} Reviews & Testimonials | Moment of Truth`;
  const defaultDesc = `Explore real reviews and testimonials from ${defaultCompanyName} customers. A simple, transparent Wall of Love built from genuine client feedback.`;

  const inputStyles = "w-full bg-[#0a0500]/50 border border-orange-500/20 rounded-xl px-4 py-3 text-zinc-200 placeholder-zinc-600 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none";

  if (loading) {
    return (
      <div className="py-20 flex justify-center w-full">
        <div className="w-10 h-10 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto pb-12"
    >
      <div className="flex items-center justify-end mb-8">
        <div className="flex items-center gap-4">
          <AnimatePresence>
            {showSuccess && (
              <motion.span 
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                className="text-green-400 text-sm flex items-center gap-1 font-medium"
              >
                <SafeIcon icon={FiCheckCircle} /> Settings Saved
              </motion.span>
            )}
          </AnimatePresence>
          <Button onClick={handleSave} disabled={isSaving} className="w-32">
            {isSaving ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><SafeIcon icon={FiSave} /> Save</>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Panel: Configuration Form */}
        <div className="lg:col-span-7 space-y-6">
          <InputCard 
            icon={FiBriefcase} 
            title="Company Identity" 
            description="Display your brand prominently on your Wall of Love."
          >
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold tracking-wider text-zinc-500 uppercase mb-2 block">Company Name</label>
                <input
                  type="text"
                  placeholder="e.g. Acme Corp"
                  className={inputStyles}
                  value={seoData.companyName}
                  onChange={(e) => setSeoData({ ...seoData, companyName: e.target.value })}
                />
              </div>
              
              <div>
                <label className="text-xs font-semibold tracking-wider text-zinc-500 uppercase mb-2 block">Company Domain</label>
                <input
                  type="text"
                  placeholder="e.g. acme.com"
                  className={`${inputStyles} ${isDomainVerified ? 'opacity-50 cursor-not-allowed' : ''}`}
                  value={seoData.companyUrl}
                  onChange={(e) => setSeoData({ ...seoData, companyUrl: e.target.value })}
                  disabled={isDomainVerified}
                />
                {isDomainVerified && (
                  <p className="text-[11px] text-green-400 mt-2 flex items-center gap-1">
                    <SafeIcon icon={FiCheckCircle} /> Domain is verified and secured.
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold tracking-wider text-zinc-500 uppercase mb-2 block">Company Logo (Max 150KB)</label>
                <div className="flex gap-4 items-center mt-2">
                  <div className="w-16 h-16 rounded-xl bg-white border border-orange-500/20 overflow-hidden flex items-center justify-center flex-shrink-0 p-2 shadow-inner">
                    {seoData.companyLogoUrl ? (
                      <img src={seoData.companyLogoUrl} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <SafeIcon icon={FiImage} className="text-zinc-400 text-2xl" />
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      ref={logoInputRef}
                      onChange={(e) => handleImageSelect(e, 'companyLogoUrl')}
                      className="hidden"
                    />
                    <Button variant="secondary" onClick={() => logoInputRef.current?.click()}>
                      Upload Logo
                    </Button>
                    {seoData.companyLogoUrl && (
                      <button 
                        onClick={() => {
                          setSeoData({ ...seoData, companyLogoUrl: '' });
                          setLogoFile(null);
                          if (logoInputRef.current) logoInputRef.current.value = '';
                        }}
                        className="p-2.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Remove logo"
                      >
                        <SafeIcon icon={FiX} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </InputCard>

          <InputCard 
            icon={FiType} 
            title="Meta Title" 
            description="The title that appears in search engine results and social media cards."
          >
            <input
              type="text"
              placeholder={defaultTitle}
              className={inputStyles}
              value={seoData.title}
              onChange={(e) => setSeoData({ ...seoData, title: e.target.value })}
              maxLength={120}
            />
            <div className="text-right mt-2 text-xs text-zinc-500">
              {seoData.title.length || defaultTitle.length}/120 characters
            </div>
          </InputCard>

          <InputCard 
            icon={FiAlignLeft} 
            title="Meta Description" 
            description="A brief summary of your page. Keep it under 160 characters for best results."
          >
            <textarea
              rows={3}
              placeholder={defaultDesc}
              className={`${inputStyles} resize-none`}
              value={seoData.description}
              onChange={(e) => setSeoData({ ...seoData, description: e.target.value })}
              maxLength={160}
            />
            <div className="text-right mt-2 text-xs text-zinc-500">
              {seoData.description.length || defaultDesc.length}/160 characters
            </div>
          </InputCard>

          <InputCard 
            icon={FiImage} 
            title="Open Graph Image" 
            description={
              <>
                The preview image shown when your link is shared on Twitter, LinkedIn, etc. 
                <span className="block mt-1 text-orange-400/80 text-xs font-medium">Optimal size: 1200 × 630 px. Max size: 400KB.</span>
              </>
            }
          >
            <div className="space-y-4">
              {seoData.ogImage && (
                <div className="relative w-32 h-20 rounded-xl overflow-hidden border border-orange-500/20 group bg-zinc-900 shadow-sm">
                  <img 
                    src={seoData.ogImage} 
                    alt="Thumbnail preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                    <button 
                      onClick={() => {
                        setSeoData({ ...seoData, ogImage: '' });
                        setOgImageFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/40 transition-colors"
                      title="Remove image"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={(e) => handleImageSelect(e, 'ogImage')}
                  className="hidden"
                />
                <Button variant="secondary" className="flex-shrink-0" onClick={() => fileInputRef.current?.click()}>
                  Upload Image
                </Button>
              </div>
            </div>
          </InputCard>
        </div>

        {/* Right Panel: Social Preview */}
        <div className="lg:col-span-5">
          <div className="sticky top-28">
            <h3 className="text-sm font-bold tracking-widest text-zinc-500 uppercase mb-4">Social Preview</h3>
            
            <div className="rounded-2xl border border-orange-500/20 shadow-[0_0_50px_rgba(234,88,12,0.05)] overflow-hidden bg-[#0a0500] group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-yellow-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
              
              {/* Image Container */}
              <div className="h-56 bg-zinc-900 border-b border-white/5 relative overflow-hidden">
                {seoData.ogImage ? (
                  <img 
                    src={seoData.ogImage} 
                    alt="Open Graph Preview" 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`absolute inset-0 flex-col items-center justify-center text-zinc-600 bg-zinc-900 ${seoData.ogImage ? 'hidden' : 'flex'}`}>
                  <SafeIcon icon={FiImage} className="text-4xl mb-2 opacity-50" />
                  <span className="text-sm font-medium">No Image Provided</span>
                </div>
              </div>
              
              {/* Text Container */}
              <div className="p-5 bg-zinc-950/80 backdrop-blur-md relative z-10">
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <SafeIcon icon={FiGlobe} /> moment-of-truth.com
                </div>
                <h4 className="text-lg font-bold text-amber-50 mb-1.5 truncate">
                  {seoData.title || defaultTitle}
                </h4>
                <p className="text-sm text-zinc-400 line-clamp-2 leading-relaxed">
                  {seoData.description || defaultDesc}
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-start gap-3">
              <SafeIcon icon={FiGlobe} className="text-blue-400 mt-1 flex-shrink-0" />
              <p className="text-sm text-blue-200/80 leading-relaxed">
                This preview approximates how your link will appear on platforms like LinkedIn, Facebook, and Twitter. Actual appearance may vary slightly by platform.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SEO;